import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { ClipLoaderCommon } from "@components";
import { breakpoint, theme } from "@utilities";
import { PATH_BOOK_NOW, PATH_CART } from "@routes";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
    AppDispatch,
    loginV2,
    setJwt,
    initializeAccountDetail,
    fetchBookNowItems,
    fetchBookNowRedeemer,
    setBookNowItemData,
    fetchRedeemer,
} from "@redux";
import { fetchCartItems } from "../checkout";
import { IAuthLogin } from "@interfaces";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { sessionHelper } from "src/Utilities/sessionHelper";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import CustomAppleSignIn from "src/Components/AppleLoginButton";
import CustomGoogleSignIn from "src/Components/GoogleLoginButton";
import { authApi } from "@api";
import { useOTPInput } from "src/hooks/useOTPInput";
import {
    generateSignupPassword,
    hasPlaceholderProfile,
    MIN_PASSWORD_LENGTH,
    signupOfferCopy,
    VERIFICATION_CODE_LENGTH,
    VERIFICATION_RESEND_SECONDS,
} from "../signupOfferConfig";
import { flagSignupDiscountApplied } from "../signupDiscountNotice";
import {
    clearGuestCartStash,
    markStashedItemAdded,
    readPendingStashedItems,
    selectItemsToReplay,
} from "../guestCartStash";
import { ensurePointsAgentUpgrade } from "../pointsAgentUpgrade";
import { isSignupEmailError, signupErrorMessage } from "src/Utilities/signupErrors";
import {
    clearRetainedPassword,
    markPasswordSet,
    retainGeneratedPassword,
} from "../signupPasswordPrompt";

interface SignInBottomSheetModalProps {
    isOpen: boolean;
    onClose: () => void;
    sessionId?: string;
    onLoginSuccess?: () => void;
    title?: string;
    subtitle?: string;
}

export const SignInBottomSheetModal = ({
    isOpen,
    onClose,
    sessionId,
    onLoginSuccess,
    title,
    subtitle,
}: SignInBottomSheetModalProps) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch<AppDispatch>();

    // "offer" is the default: name/email for the discount, plus the social,
    // guest and log-in alternatives. "verify" is the emailed code. "login" is
    // the existing password form for people who already have an account, and
    // "reset" is that form's forgotten-password route.
    const [step, setStep] = useState<
        "offer" | "details" | "verify" | "password" | "login" | "reset"
    >("offer");

    const [offerEmail, setOfferEmail] = useState("");
    const [isClaiming, setIsClaiming] = useState(false);

    /**
     * Collected before the account exists, not after.
     *
     * The backend mints the points agent's branch identity (e.g.
     * `ft-DWF-guesttraveller`) from the name on the account and never revises it,
     * so an account created nameless is stuck with the stand-in for good. These
     * go out with the sign-up itself and again with the profile write that
     * precedes the upgrade.
     */
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [isSavingPassword, setIsSavingPassword] = useState(false);
    /**
     * Set once the password step is done with, while the sign-up finishes and the
     * checkout is fetched. Freezes that step's fields and buttons so a second
     * click cannot re-enter the hand-off, and raises the progress notice over
     * the top — the wait is several requests long and otherwise looks like nothing
     * happened.
     */
    const [isFinishing, setIsFinishing] = useState(false);
    /**
     * The password the account was created with, which the visitor never sees.
     * Kept so the change-password call has a `current_password` to send once they
     * choose their own.
     */
    const generatedPasswordRef = useRef("");
    /**
     * The upgrade kicked off at verification, running while the password screen is
     * up. Awaited before the hand-off so the checkout is never reached mid-upgrade.
     */
    const upgradeInFlightRef = useRef<Promise<void> | null>(null);

    const [otp, setOtp] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    const [resendIn, setResendIn] = useState(VERIFICATION_RESEND_SECONDS);
    const { inputRefs, handleInputChange, handlePaste } = useOTPInput(otp, setOtp);

    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    /**
     * Forgotten-password state. The reset runs inside the sheet rather than
     * sending the visitor to /forgot-password, because leaving takes the checkout
     * with it — the cart, the pending Book Now and the sheet itself all go.
     */
    const [isSendingReset, setIsSendingReset] = useState(false);
    const [isResetCodeVerified, setIsResetCodeVerified] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    // Resend countdown, restarted whenever the code screen is entered.
    // Runs for the reset screen too, which reuses this countdown for its own
    // resend link.
    useEffect(() => {
        if ((step !== "verify" && step !== "reset") || resendIn <= 0) return;
        const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [step, resendIn]);

    if (!isOpen) return null;

    const handleLoginSuccessInternal = async () => {
        const { lsJwt } = await import("src/Utilities/storage");
        const jwtData = lsJwt.getItem();

        if (!jwtData) {
            throw new Error("No authentication token found");
        }

        try {
            const isBookNow = location.pathname.includes(PATH_BOOK_NOW);
            const isCart = location.pathname.includes(PATH_CART);

            let bookNowSessionId: string | undefined = undefined;
            let cartSessionId: string | undefined = undefined;

            if (isBookNow) {
                const urlSessionId = searchParams.get("sessionId");
                bookNowSessionId = urlSessionId || undefined;
                if (!bookNowSessionId && sessionHelper.hasBookNowSession()) {
                    bookNowSessionId = sessionHelper.getBookNowSessionId();
                }
                if (!bookNowSessionId && sessionId) {
                    bookNowSessionId = sessionId;
                }
                if (bookNowSessionId && !urlSessionId) {
                    setSearchParams({ sessionId: bookNowSessionId });
                }
            }

            // Resolved wherever the sheet was raised, not only on the cart page.
            // Book Now opens it from a product page, where neither branch matched —
            // so a guest who had already added something signed up into an empty
            // account and their cart stayed behind in the guest session.
            if (!isBookNow) {
                const urlSessionId = searchParams.get("sessionId");
                cartSessionId = urlSessionId || undefined;
                if (!cartSessionId && sessionHelper.hasSession()) {
                    cartSessionId = sessionHelper.getSessionId();
                }
                if (!cartSessionId && sessionId) {
                    cartSessionId = sessionId;
                }
                // Only the cart page needs the id in its URL, and it has to be there
                // BEFORE the JWT is set or Cart.tsx re-renders without it.
                if (isCart && cartSessionId && !urlSessionId) {
                    setSearchParams({ sessionId: cartSessionId });
                }
            }

            const axiosInstance = axios.create({
                baseURL: process.env.REACT_APP_API_URL_V2,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwtData.accessToken}`,
                },
            });

            if (isBookNow && bookNowSessionId) {
                try {
                    await axiosInstance.put(`/cart/convert/session/${bookNowSessionId}`);
                } catch (error) {
                    console.error("Failed to merge BookNow cart session:", error);
                }
            }

            let cartMergeSucceeded = false;
            if (cartSessionId) {
                try {
                    await axiosInstance.put(`/cart/convert/session/${cartSessionId}`);
                    cartMergeSucceeded = true;
                } catch (error: any) {
                    console.error(
                        "Failed to merge Cart session:",
                        error?.response?.status,
                        error?.response?.data,
                    );
                }
            }

            // The convert above currently answers 200 and moves nothing, so the
            // account can still be empty here. Every guest add was recorded as the
            // payload that created it; anything the convert did not bring across is
            // posted again against the new account. Skipped per item rather than
            // wholesale, so this stays correct once the endpoint is fixed.
            try {
                const stashed = readPendingStashedItems();
                if (stashed.length > 0) {
                    const after = await axiosInstance.get("/cart/items");
                    const payload = after.data?.data;
                    const rows = Array.isArray(payload) ? payload : (payload?.items ?? []);
                    const toReplay = selectItemsToReplay(stashed, rows);

                    for (const entry of toReplay) {
                        try {
                            // Sequential on purpose: the backend re-checks
                            // availability per add, and firing them together has
                            // them compete for the same inventory.
                            // eslint-disable-next-line no-await-in-loop
                            await axiosInstance.post("/cart/items/v2", entry.item);
                            markStashedItemAdded(entry.id);
                        } catch (replayError: any) {
                            // One product failing its availability re-check must not
                            // cost the visitor the rest of the cart.
                            console.error(
                                "Failed to re-add a guest cart item after sign-up:",
                                entry.id,
                                replayError?.response?.data,
                            );
                        }
                    }
                }
                clearGuestCartStash();
            } catch (stashError) {
                console.error("Guest cart replay failed after sign-up:", stashError);
            }

            dispatch(setJwt(jwtData));

            const freshAccount = await dispatch(initializeAccountDetail()).unwrap();

            if (isBookNow && bookNowSessionId) {
                const fetchedItems = await dispatch(fetchBookNowItems(bookNowSessionId)).unwrap();
                dispatch(setBookNowItemData(fetchedItems));
                const redeemers = await dispatch(fetchBookNowRedeemer(bookNowSessionId)).unwrap();

                // A profile still holding the sign-up stand-ins passes every field
                // test below, so it would seed a traveller with a 1900 date of
                // birth and a zeroed phone number. Those details belong to nobody
                // and must not be offered as a saved traveller.
                const hasCompleteProfile =
                    freshAccount?.title &&
                    freshAccount?.first_name &&
                    freshAccount?.last_name &&
                    freshAccount?.email &&
                    freshAccount?.phone_number &&
                    freshAccount?.post_code &&
                    freshAccount?.country_code &&
                    !hasPlaceholderProfile(freshAccount);

                const userAlreadyExists = redeemers.some(
                    (r) => r.email?.toLowerCase() === freshAccount?.email?.toLowerCase(),
                );

                if (hasCompleteProfile && !userAlreadyExists) {
                    try {
                        const { redeemerApi } = await import("src/Api/redeemers");
                        const userTravellerData = {
                            title: freshAccount.title!,
                            firstName: freshAccount.first_name!,
                            lastName: freshAccount.last_name!,
                            email: freshAccount.email!,
                            phoneNumber: freshAccount.phone_number!,
                            postalCode: freshAccount.post_code!,
                            countryCode: freshAccount.country_code!,
                            dateOfBirth: freshAccount.date_of_birth || "",
                            customerIndex: 0,
                        };
                        await redeemerApi.AddRedeemerBookNow(userTravellerData, bookNowSessionId);
                        await dispatch(fetchBookNowRedeemer(bookNowSessionId)).unwrap();
                    } catch (error) {
                        console.error("Failed to add user as BookNow redeemer:", error);
                    }
                }
            }

            if (isCart && cartSessionId) {
                const effectiveSessionId = cartMergeSucceeded ? undefined : cartSessionId;
                await dispatch(fetchCartItems(effectiveSessionId)).unwrap();
                const redeemers = await dispatch(fetchRedeemer(effectiveSessionId)).unwrap();

                // A profile still holding the sign-up stand-ins passes every field
                // test below, so it would seed a traveller with a 1900 date of
                // birth and a zeroed phone number. Those details belong to nobody
                // and must not be offered as a saved traveller.
                const hasCompleteProfile =
                    freshAccount?.title &&
                    freshAccount?.first_name &&
                    freshAccount?.last_name &&
                    freshAccount?.email &&
                    freshAccount?.phone_number &&
                    freshAccount?.post_code &&
                    freshAccount?.country_code &&
                    !hasPlaceholderProfile(freshAccount);

                const userAlreadyExists = redeemers.some(
                    (r) => r.email?.toLowerCase() === freshAccount?.email?.toLowerCase(),
                );

                if (hasCompleteProfile && !userAlreadyExists) {
                    try {
                        const { redeemerApi } = await import("src/Api/redeemers");
                        const userTravellerData = {
                            title: freshAccount.title!,
                            firstName: freshAccount.first_name!,
                            lastName: freshAccount.last_name!,
                            email: freshAccount.email!,
                            phoneNumber: freshAccount.phone_number!,
                            postalCode: freshAccount.post_code!,
                            countryCode: freshAccount.country_code!,
                            dateOfBirth: freshAccount.date_of_birth || "",
                            customerIndex: 0,
                        };
                        await redeemerApi.AddRedeemer(userTravellerData, effectiveSessionId);
                        await dispatch(fetchRedeemer(effectiveSessionId)).unwrap();
                    } catch (error) {
                        console.error("Failed to add user as Cart redeemer:", error);
                    }
                }

                sessionHelper.clearSession();
                if (cartMergeSucceeded) {
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete("sessionId");
                    setSearchParams(newParams, { replace: true });
                }
            }

            // The cart page clears its own session inside the block above, once it
            // has refetched through it. Everywhere else there is nothing to refetch,
            // so the spent guest session is dropped here instead.
            if (cartMergeSucceeded && !isCart) {
                sessionHelper.clearSession();
            }

            // Clear book-now session similarly
            if (isBookNow && bookNowSessionId) {
                sessionHelper.clearBookNowSession();
            }

            onLoginSuccess?.();
        } catch (error: any) {
            ErrorToast({ Message: error?.response?.data?.title || "Failed to load user details" });
            throw error;
        }
    };

    // Email validation helper - only allows alphanumeric, dots, underscores, plus, and hyphens
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    /**
     * Start the forgotten-password route: email the code, then show the reset
     * screen. The address is taken from the login form, so it is the one the
     * visitor was already trying.
     */
    const handleForgotPassword = async () => {
        if (!loginEmail || !isValidEmail(loginEmail)) {
            ErrorToast({ Message: "Enter your email address first, then choose Forgot password" });
            return;
        }
        setIsSendingReset(true);
        try {
            await authApi.forgotPassEmail({ email: loginEmail });
            setOtp("");
            setIsResetCodeVerified(false);
            setNewPassword("");
            setConfirmPassword("");
            setResendIn(VERIFICATION_RESEND_SECONDS);
            setStep("reset");
        } catch (error: any) {
            ErrorToast({
                Message: error?.response?.data?.title || "We couldn't send that code",
            });
        } finally {
            setIsSendingReset(false);
        }
    };

    const handleResendResetCode = async () => {
        if (resendIn > 0) return;
        try {
            await authApi.forgotPassEmail({ email: loginEmail });
            setResendIn(VERIFICATION_RESEND_SECONDS);
        } catch (error: any) {
            ErrorToast({ Message: error?.response?.data?.title || "Could not resend the code" });
        }
    };

    const handleVerifyResetCode = async () => {
        if (otp.length !== VERIFICATION_CODE_LENGTH) {
            ErrorToast({ Message: `Please enter the ${VERIFICATION_CODE_LENGTH}-digit code` });
            return;
        }
        setIsVerifying(true);
        try {
            await authApi.verifyEmail({ otp, email: loginEmail });
            setIsResetCodeVerified(true);
        } catch (error: any) {
            ErrorToast({
                Message:
                    error?.response?.data?.data?.otp?.[0] ||
                    error?.response?.data?.title ||
                    "That code was not accepted",
            });
        } finally {
            setIsVerifying(false);
        }
    };

    /**
     * Save the new password and carry straight on into the sign-in the visitor
     * was attempting, so a reset does not cost them the booking they were in the
     * middle of. The code is sent again alongside the password: the reset
     * endpoint takes `otp` and `email` as well as the new password.
     */
    const handleSaveResetPassword = async () => {
        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            ErrorToast({ Message: signupOfferCopy.passwordHint });
            return;
        }
        if (newPassword !== confirmPassword) {
            ErrorToast({ Message: "Those passwords do not match" });
            return;
        }
        setIsResetting(true);
        try {
            await authApi.forgotPass({ new_password: newPassword, otp, email: loginEmail } as any);

            // Recorded before anything that can fail. The password is already the
            // visitor's own by this point, so this device stops showing the "Set
            // password" badge even if the steps below do not land.
            markPasswordSet(loginEmail);

            setLoginPassword(newPassword);
            setStep("login");
            await handleEmailLogin(newPassword);

            // The reset endpoint takes no flag, so the account still reads as
            // holding a generated password and the badge and menu entry would stay.
            // A no-op change clears it — possible only now, because it needs the
            // JWT the sign-in above just stored, and the current password is the
            // one we set moments ago. Best effort: failing here leaves a working
            // password and a stale prompt, not a failed reset.
            try {
                await authApi.changePass({
                    new_password: newPassword,
                    current_password: newPassword,
                    is_password_randomly_generated: false,
                });
                await dispatch(initializeAccountDetail()).unwrap();
            } catch (flagError: any) {
                console.error(
                    "Clearing the generated-password flag after a reset failed:",
                    flagError?.response?.status,
                    flagError?.response?.data,
                );
            }
        } catch (error: any) {
            ErrorToast({
                Message: error?.response?.data?.title || "We couldn't reset that password",
            });
        } finally {
            setIsResetting(false);
        }
    };

    const handleEmailLogin = async (passwordOverride?: string) => {
        const password = passwordOverride ?? loginPassword;
        if (!loginEmail || !password) {
            ErrorToast({ Message: "Please enter both email and password" });
            return;
        }

        if (!isValidEmail(loginEmail)) {
            ErrorToast({ Message: "Please enter a valid email address" });
            return;
        }

        setIsLoggingIn(true);
        try {
            const loginRes = (await dispatch(loginV2({ email: loginEmail, password }))) as {
                payload: IAuthLogin;
            };

            if (!loginRes.payload.data?.accessToken) {
                ErrorToast({ Message: "Please confirm username and password are correct" });
                return;
            }

            const { lsJwt, lsEmail } = await import("src/Utilities/storage");
            lsJwt.setItem(loginRes.payload.data);
            lsEmail.setItem(loginEmail);

            // Complete session merge and cart refresh BEFORE closing modal
            // handleLoginSuccessInternal sets sessionId in URL first, THEN sets JWT in Redux
            // This prevents race condition where Cart.tsx re-fetches before session is converted
            await handleLoginSuccessInternal();

            setLoginPassword("");
            onClose();
        } catch (error: any) {
            ErrorToast({
                Message: error?.response?.data?.title || "An error occurred during login",
            });
        } finally {
            setIsLoggingIn(false);
        }
    };

    /**
     * Finish a path that produced a signed-in account.
     *
     * Deliberately does not navigate or touch the cart. handleLoginSuccessInternal
     * ends by calling onLoginSuccess, which is the panel's handleSignInSuccess:
     * that replays the action the visitor originally clicked, so Book Now adds the
     * item as a signed-in user and moves to the book-now checkout itself. Doing
     * our own merge and navigation raced that and landed on an empty cart — the
     * item does not exist on any session until the replay runs.
     */
    const completeSignedInPath = async () => {
        flagSignupDiscountApplied();
        await handleLoginSuccessInternal();
    };

    /**
     * Email path. Creates the account with a generated password — the visitor is
     * never asked for one here — then sends them to the emailed code.
     * `skip_otp: false` so the backend issues the code this screen expects.
     */
    const handleContinueFromEmail = () => {
        if (!isValidEmail(offerEmail)) {
            ErrorToast({ Message: "Please enter a valid email address" });
            return;
        }
        setStep("details");
    };

    const handleCreateAccount = async () => {
        if (!firstName.trim() || !lastName.trim()) {
            ErrorToast({ Message: "Please enter your first and last name" });
            return;
        }

        setIsClaiming(true);
        try {
            // Random password: the account is created on the visitor's behalf and
            // they authenticate by the emailed code, so there is nothing to choose.
            const password = generateSignupPassword();
            generatedPasswordRef.current = password;
            const res = await authApi.signUp({
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                email: offerEmail.trim(),
                password,
                skip_otp: false,
                is_password_randomly_generated: true,
            });

            if (res.data.code !== 200) {
                // The endpoint refuses in two shapes; this is the one that answers
                // 200-with-a-code rather than throwing.
                const refusal = { response: { data: res.data } };
                if (isSignupEmailError(refusal)) setStep("offer");
                ErrorToast({ Message: signupErrorMessage(refusal) });
                return;
            }

            const loginRes = (await dispatch(loginV2({ email: offerEmail.trim(), password }))) as {
                payload: IAuthLogin;
            };

            if (loginRes.payload.data?.accessToken) {
                const { lsJwt, lsEmail } = await import("src/Utilities/storage");
                lsJwt.setItem(loginRes.payload.data);
                lsEmail.setItem(offerEmail.trim());
                // The token has to reach Redux here, not just localStorage.
                // ftApiClient's request interceptor reads it from
                // store.getState().auth.jwt, and the auth slice only picks
                // localStorage up once, when the store is created — so a write to
                // lsJwt now is invisible to it. Without this dispatch every
                // authenticated call on the verify step (the profile write, the
                // points upgrade, the resend-code link) goes out with no
                // Authorization header and is rejected as a guest request.
                //
                // Safe this early because useIsLoggedIn also requires an account,
                // which initializeAccountDetail has not loaded yet: the app does
                // not yet consider anyone signed in, so the email-verification
                // routing guard cannot fire while this sheet is still open.
                dispatch(setJwt(loginRes.payload.data));
            }

            setResendIn(VERIFICATION_RESEND_SECONDS);
            setStep("verify");
        } catch (error: any) {
            // An address already registered is the one refusal the visitor can act
            // on, and it belongs to the screen before this one.
            if (isSignupEmailError(error)) setStep("offer");
            ErrorToast({ Message: signupErrorMessage(error) });
        } finally {
            setIsClaiming(false);
        }
    };

    const handleVerifyCode = async () => {
        if (otp.length !== VERIFICATION_CODE_LENGTH) {
            ErrorToast({ Message: `Please enter the ${VERIFICATION_CODE_LENGTH}-digit code` });
            return;
        }
        setIsVerifying(true);
        try {
            const res = await authApi.verifyEmail({ otp, email: offerEmail.trim() });
            if (res.data.code !== 200) {
                ErrorToast({ Message: res.data.title || "That code was not accepted" });
                return;
            }
            // Started, deliberately not awaited. The upgrade is the slow part of
            // this flow — a profile write plus a TDMS round trip — and running it
            // underneath the password screen is what removes the dead wait the
            // visitor used to sit through. It swallows its own errors, so there is
            // no unhandled rejection to worry about; the hand-off awaits it.
            upgradeInFlightRef.current = upgradeNewAccountToPointsAgent();
            setStep("password");
        } catch (error: any) {
            ErrorToast({
                Message: error?.response?.data?.title || "That code was not accepted",
            });
        } finally {
            setIsVerifying(false);
        }
    };

    /**
     * Leave the sheet and let the panel replay the action that opened it, which is
     * what carries the visitor to the checkout.
     *
     * Waits on the upgrade first: it has had the whole password screen to finish,
     * so this is normally instant, but arriving at the checkout mid-upgrade would
     * show totals that are about to change.
     */
    const finishSignup = async () => {
        await upgradeInFlightRef.current;
        upgradeInFlightRef.current = null;
        try {
            await completeSignedInPath();
        } catch {
            // handleLoginSuccessInternal has already surfaced the error. Swallowed
            // here because this runs from click handlers and from the overlay,
            // where a rejection would go unhandled.
        }
    };

    /**
     * Carry on without a password.
     *
     * Nothing is emailed: the only message available here is a bare verification
     * code from the forgotten-password flow, and one of those arriving unannounced
     * minutes after sign-up reads as a phishing attempt. The account keeps
     * `is_password_randomly_generated`, which the header turns into a "Set
     * password" prompt for as long as it is outstanding.
     */
    /** Everything on the password step is inert while the hand-off runs. */
    const isPasswordStepBusy = isSavingPassword || isFinishing;

    const handleSkipPassword = async () => {
        // Held in memory so the header's "Set password" prompt can still change it
        // directly. Nothing is emailed: the only message available is a bare
        // verification code, and one arriving unannounced reads as phishing. Once
        // this is lost to a reload the prompt asks for a code instead, because the
        // backend requires a current password for a direct change.
        retainGeneratedPassword({
            email: offerEmail.trim(),
            currentPassword: generatedPasswordRef.current,
        });
        setIsFinishing(true);
        try {
            await finishSignup();
        } finally {
            // Normally unreachable — the replay navigates and this unmounts — but
            // if the hand-off fails the step has to become usable again.
            setIsFinishing(false);
        }
    };

    const handleCreatePassword = async () => {
        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            ErrorToast({ Message: `Please use at least ${MIN_PASSWORD_LENGTH} characters` });
            return;
        }
        if (newPassword !== confirmPassword) {
            ErrorToast({ Message: "Those passwords do not match" });
            return;
        }

        let saved = false;
        setIsSavingPassword(true);
        try {
            // The account was created with a password the visitor never saw, so it
            // goes in as the current one. The flag is the state to store, not a
            // statement about the password being replaced: sending `true` here
            // marked the brand-new password as generated too, and left the "Set
            // password" prompt up forever.
            await authApi.changePass({
                new_password: newPassword,
                current_password: generatedPasswordRef.current,
                is_password_randomly_generated: false,
            });
            saved = true;
            // Recorded here as well as in the app-level prompt: this is the other
            // place a real password gets set, and without it a backend flag that
            // fails to clear leaves the "Set password" badge up for good.
            markPasswordSet(offerEmail.trim());
            clearRetainedPassword();
        } catch (error: any) {
            // A password that would not save must not hold up the booking, so they
            // are let through with the emailed link as the way back in.
            ErrorToast({
                Message:
                    error?.response?.data?.title ||
                    "We couldn't save that password — you can set one from your account",
            });
        } finally {
            setIsSavingPassword(false);
        }

        // Outside the try/catch on purpose: the hand-off must run exactly once,
        // whether or not the password saved. Inside, a failure here would re-enter
        // it from the catch and replay the cart merge twice.
        if (!saved) {
            await handleSkipPassword();
            return;
        }
        setIsFinishing(true);
        try {
            await finishSignup();
        } finally {
            setIsFinishing(false);
        }
    };

    const handleResendCode = async () => {
        if (resendIn > 0) return;
        try {
            await authApi.resendVerificationOtp();
            setResendIn(VERIFICATION_RESEND_SECONDS);
        } catch (error: any) {
            ErrorToast({ Message: error?.response?.data?.title || "Could not resend the code" });
        }
    };

    /**
     * Turn the brand-new account into a points agent straight away, so the
     * checkout shows points from the first screen rather than after the traveller
     * form is saved.
     *
     * No account is passed: this one was created moments ago from an email address
     * alone, so every profile field the backend's gate wants is a stand-in.
     */
    const upgradeNewAccountToPointsAgent = async (): Promise<void> => {
        await ensurePointsAgentUpgrade({
            email: offerEmail.trim(),
            signupNames: { first_name: firstName.trim(), last_name: lastName.trim() },
            dispatch,
            // Surfaced rather than swallowed: silence here is what hid a 401 on
            // this path, and the booking is fine either way.
            onError: (Message) => ErrorToast({ Message }),
        });
    };

    /**
     * Apple and Google paths. The provider has already verified the address, so
     * there is no emailed code — they go straight to the cart.
     */
    const handleSocialSuccess = async () => {
        try {
            await completeSignedInPath();
        } catch {
            // handleLoginSuccessInternal has already surfaced the error.
        }
    };

    const handleBackToMain = () => {
        setStep("offer");
        setLoginEmail("");
        setLoginPassword("");
    };

    return (
        <>
            {/* Dismissing during the password step must not strand the visitor:
                the account exists and is verified by then, so closing completes
                the sign-up and carries on to the checkout rather than dropping
                them back on the product page with nothing added. It counts as
                skipping, so the setup email goes out. */}
            <Overlay
                onClick={() => {
                    if (step === "password") {
                        if (!isPasswordStepBusy) void handleSkipPassword();
                        return;
                    }
                    onClose();
                }}
            />
            {/* Sits above the frozen password step rather than replacing it, so the
                sheet does not visibly collapse and rebuild during the hand-off.
                Unmounts by itself when the replayed action navigates away. */}
            {isFinishing && (
                <FinishingDialog>
                    <ClipLoaderCommon color={theme.colors.main} size={28} margin={0} />
                    <FinishingTitle>{signupOfferCopy.finishingHeadline}</FinishingTitle>
                </FinishingDialog>
            )}
            <BottomSheet onClick={(e) => e.stopPropagation()}>
                {step === "offer" && (
                    <MainContent>
                        <CloseButton onClick={onClose} aria-label="Close">
                            &times;
                        </CloseButton>
                        <BrandLogo src="/images/logos/Primary_logo_color.svg" alt="" />
                        <OfferHeadline>{title || signupOfferCopy.headline}</OfferHeadline>
                        {subtitle && <Subtitle>{subtitle}</Subtitle>}

                        <OfferField
                            type="email"
                            placeholder="Email address"
                            value={offerEmail}
                            onChange={(e) => setOfferEmail(e.target.value)}
                            disabled={isClaiming}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !isClaiming) handleContinueFromEmail();
                            }}
                        />

                        <ClaimButton onClick={handleContinueFromEmail} disabled={isClaiming}>
                            {isClaiming ? (
                                <ClipLoaderCommon
                                    color={theme.colors.button_text}
                                    size={22}
                                    margin={0}
                                />
                            ) : (
                                signupOfferCopy.claimButton
                            )}
                        </ClaimButton>

                        <OrDivider>
                            <span>OR</span>
                        </OrDivider>

                        <SocialRow>
                            <CustomAppleSignIn onBeforeLogin={() => Promise.resolve(true)} />
                        </SocialRow>
                        <SocialRow>
                            <CustomGoogleSignIn
                                preventRedirect
                                onLoginSuccess={handleSocialSuccess}
                            />
                        </SocialRow>

                        <LoginPrompt>
                            {signupOfferCopy.loginPrompt}{" "}
                            <LoginLink onClick={() => setStep("login")}>Log in</LoginLink>
                        </LoginPrompt>
                    </MainContent>
                )}

                {step === "details" && (
                    <MainContent>
                        <BackButton onClick={() => setStep("offer")} aria-label="Back">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M15 18L9 12L15 6"
                                    stroke="#131214"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </BackButton>
                        <CloseButton onClick={onClose} aria-label="Close">
                            &times;
                        </CloseButton>
                        <BrandLogo src="/images/logos/Primary_logo_color.svg" alt="" />
                        <OfferHeadline>{signupOfferCopy.detailsHeadline}</OfferHeadline>
                        <Subtitle>{signupOfferCopy.detailsSubtitle}</Subtitle>

                        <OfferField
                            type="text"
                            autoComplete="given-name"
                            placeholder="First name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            disabled={isClaiming}
                        />
                        <OfferField
                            type="text"
                            autoComplete="family-name"
                            placeholder="Last name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            disabled={isClaiming}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !isClaiming) handleCreateAccount();
                            }}
                        />

                        <ClaimButton
                            onClick={handleCreateAccount}
                            disabled={isClaiming || !firstName.trim() || !lastName.trim()}
                        >
                            {isClaiming ? (
                                <ClipLoaderCommon
                                    color={theme.colors.button_text}
                                    size={22}
                                    margin={0}
                                />
                            ) : (
                                signupOfferCopy.detailsButton
                            )}
                        </ClaimButton>
                    </MainContent>
                )}

                {step === "verify" && (
                    <MainContent>
                        <BackButton onClick={() => setStep("offer")} aria-label="Back">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M15 18L9 12L15 6"
                                    stroke="#131214"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </BackButton>
                        <CloseButton onClick={onClose} aria-label="Close">
                            &times;
                        </CloseButton>
                        <BrandLogo src="/images/logos/Primary_logo_color.svg" alt="" />
                        <OfferHeadline>{signupOfferCopy.verifyHeadline}</OfferHeadline>
                        <Subtitle>{signupOfferCopy.verifySubtitle(offerEmail)}</Subtitle>
                        <ChangeEmailLink onClick={() => setStep("offer")}>
                            Change email
                        </ChangeEmailLink>

                        <OtpRow>
                            {Array.from({ length: VERIFICATION_CODE_LENGTH }).map((_, i) => (
                                <OtpBox
                                    key={i}
                                    ref={(el) => {
                                        inputRefs.current[i] = el;
                                    }}
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={otp[i] ?? ""}
                                    onPaste={handlePaste}
                                    onChange={(e) => {
                                        const next =
                                            otp.slice(0, i) + e.target.value + otp.slice(i + 1);
                                        handleInputChange({
                                            ...e,
                                            target: { ...e.target, value: next },
                                        } as React.ChangeEvent<HTMLInputElement>);
                                        if (e.target.value && inputRefs.current[i + 1]) {
                                            inputRefs.current[i + 1]!.focus();
                                        }
                                    }}
                                />
                            ))}
                        </OtpRow>

                        <ResendRow>
                            Didn&apos;t get the code?{" "}
                            <ResendLink onClick={handleResendCode} $disabled={resendIn > 0}>
                                {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend"}
                            </ResendLink>
                        </ResendRow>

                        <ClaimButton
                            onClick={handleVerifyCode}
                            disabled={isVerifying || otp.length !== VERIFICATION_CODE_LENGTH}
                        >
                            {isVerifying ? (
                                <ClipLoaderCommon
                                    color={theme.colors.button_text}
                                    size={22}
                                    margin={0}
                                />
                            ) : (
                                "Verify and continue"
                            )}
                        </ClaimButton>
                    </MainContent>
                )}

                {step === "password" && (
                    <MainContent>
                        {/* No back button and no close: the account exists and is
                            verified by this point, so there is nothing to go back
                            to, and the flow continues either way. */}
                        <BrandLogo src="/images/logos/Primary_logo_color.svg" alt="" />
                        <OfferHeadline>{signupOfferCopy.passwordHeadline}</OfferHeadline>
                        <Subtitle>{signupOfferCopy.passwordSubtitle}</Subtitle>

                        <PasswordWrapper>
                            <PasswordInput
                                type={showNewPassword ? "text" : "password"}
                                placeholder="Create a password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={isPasswordStepBusy}
                                autoFocus
                            />
                            <EyeIcon onClick={() => setShowNewPassword(!showNewPassword)}>
                                {showNewPassword ? (
                                    <VisibilityOutlined />
                                ) : (
                                    <VisibilityOffOutlined />
                                )}
                            </EyeIcon>
                        </PasswordWrapper>
                        {/* Stated up front rather than only as an error on submit,
                            so the rule is known before the field is filled in. */}
                        <PasswordHint>{signupOfferCopy.passwordHint}</PasswordHint>

                        <PasswordWrapper>
                            <PasswordInput
                                type={showNewPassword ? "text" : "password"}
                                placeholder="Confirm password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={isPasswordStepBusy}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !isPasswordStepBusy) {
                                        handleCreatePassword();
                                    }
                                }}
                            />
                        </PasswordWrapper>

                        <ClaimButton onClick={handleCreatePassword} disabled={isPasswordStepBusy}>
                            {isSavingPassword ? (
                                <ClipLoaderCommon
                                    color={theme.colors.button_text}
                                    size={22}
                                    margin={0}
                                />
                            ) : (
                                signupOfferCopy.passwordButton
                            )}
                        </ClaimButton>

                        {/* Goes the same way as dismissing the sheet: the sign-up
                            completes and the visitor carries on to the checkout,
                            with the emailed setup link as the way back in. */}
                        <SkipLink
                            type="button"
                            onClick={() => {
                                void handleSkipPassword();
                            }}
                            disabled={isPasswordStepBusy}
                        >
                            {signupOfferCopy.passwordSkip}
                        </SkipLink>
                    </MainContent>
                )}

                {step === "reset" && (
                    <MainContent>
                        <BackButton onClick={() => setStep("login")} aria-label="Back">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M15 18L9 12L15 6"
                                    stroke="#131214"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </BackButton>
                        <CloseButton onClick={onClose} aria-label="Close">
                            &times;
                        </CloseButton>
                        <OfferHeadline>Reset your password</OfferHeadline>
                        <Subtitle>{signupOfferCopy.verifySubtitle(loginEmail)}</Subtitle>

                        {!isResetCodeVerified ? (
                            <>
                                <OtpRow>
                                    {Array.from({ length: VERIFICATION_CODE_LENGTH }).map(
                                        (_, i) => (
                                            <OtpBox
                                                key={i}
                                                ref={(el) => {
                                                    inputRefs.current[i] = el;
                                                }}
                                                inputMode="numeric"
                                                maxLength={1}
                                                value={otp[i] ?? ""}
                                                onPaste={handlePaste}
                                                onChange={(e) => {
                                                    const next =
                                                        otp.slice(0, i) +
                                                        e.target.value +
                                                        otp.slice(i + 1);
                                                    handleInputChange({
                                                        ...e,
                                                        target: { ...e.target, value: next },
                                                    } as React.ChangeEvent<HTMLInputElement>);
                                                    if (
                                                        e.target.value &&
                                                        inputRefs.current[i + 1]
                                                    ) {
                                                        inputRefs.current[i + 1]!.focus();
                                                    }
                                                }}
                                            />
                                        ),
                                    )}
                                </OtpRow>

                                <ResendRow>
                                    Didn&apos;t get the code?{" "}
                                    <ResendLink
                                        onClick={handleResendResetCode}
                                        $disabled={resendIn > 0}
                                    >
                                        {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend"}
                                    </ResendLink>
                                </ResendRow>

                                <ClaimButton
                                    onClick={handleVerifyResetCode}
                                    disabled={
                                        isVerifying || otp.length !== VERIFICATION_CODE_LENGTH
                                    }
                                >
                                    {isVerifying ? (
                                        <ClipLoaderCommon
                                            color={theme.colors.button_text}
                                            size={22}
                                            margin={0}
                                        />
                                    ) : (
                                        "Verify code"
                                    )}
                                </ClaimButton>
                            </>
                        ) : (
                            <>
                                <InputLabel>New password</InputLabel>
                                <PasswordWrapper>
                                    <PasswordInput
                                        type={showNewPassword ? "text" : "password"}
                                        placeholder="Enter a new password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        disabled={isResetting}
                                    />
                                    <EyeIcon onClick={() => setShowNewPassword(!showNewPassword)}>
                                        {showNewPassword ? (
                                            <VisibilityOutlined />
                                        ) : (
                                            <VisibilityOffOutlined />
                                        )}
                                    </EyeIcon>
                                </PasswordWrapper>

                                <InputLabel>Confirm password</InputLabel>
                                <PasswordWrapper>
                                    <PasswordInput
                                        type={showNewPassword ? "text" : "password"}
                                        placeholder="Enter it again"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        disabled={isResetting}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !isResetting) {
                                                handleSaveResetPassword();
                                            }
                                        }}
                                    />
                                </PasswordWrapper>
                                <PasswordHint>{signupOfferCopy.passwordHint}</PasswordHint>

                                <ClaimButton
                                    onClick={handleSaveResetPassword}
                                    disabled={isResetting || !newPassword || !confirmPassword}
                                >
                                    {isResetting ? (
                                        <ClipLoaderCommon
                                            color={theme.colors.button_text}
                                            size={22}
                                            margin={0}
                                        />
                                    ) : (
                                        "Save and sign in"
                                    )}
                                </ClaimButton>
                            </>
                        )}
                    </MainContent>
                )}

                {step === "login" && (
                    <EmailLoginContent>
                        <BackButton onClick={handleBackToMain}>
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M19 12H5M5 12L12 19M5 12L12 5"
                                    stroke="#131214"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </BackButton>
                        <Title>Sign in with Email</Title>
                        <Subtitle>Enter your email and password to sign in.</Subtitle>

                        <InputLabel>Email</InputLabel>
                        <EmailInput
                            type="email"
                            placeholder="Enter your email"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            disabled={isLoggingIn}
                        />

                        <InputLabel>Password</InputLabel>
                        <PasswordWrapper>
                            <PasswordInput
                                type={showLoginPassword ? "text" : "password"}
                                placeholder="Enter your password"
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                disabled={isLoggingIn}
                                onKeyDown={(e) => {
                                    if (
                                        e.key === "Enter" &&
                                        loginEmail &&
                                        loginPassword &&
                                        !isLoggingIn
                                    ) {
                                        handleEmailLogin();
                                    }
                                }}
                            />
                            <EyeIcon onClick={() => setShowLoginPassword(!showLoginPassword)}>
                                {showLoginPassword ? (
                                    <VisibilityOutlined />
                                ) : (
                                    <VisibilityOffOutlined />
                                )}
                            </EyeIcon>
                        </PasswordWrapper>

                        <ForgotPasswordLink
                            onClick={handleForgotPassword}
                            $disabled={isSendingReset || isLoggingIn}
                        >
                            {isSendingReset ? "Sending code…" : "Forgot password?"}
                        </ForgotPasswordLink>

                        <LoginButton
                            onClick={() => handleEmailLogin()}
                            disabled={!loginEmail || !loginPassword || isLoggingIn}
                            $isBusy={isLoggingIn}
                        >
                            {isLoggingIn ? (
                                /* White rather than the brand's button text, which
                                   is black on Peterpans and would barely show on
                                   the grey. */
                                <ClipLoaderCommon color="#fff" size={24} margin={0} />
                            ) : (
                                "Sign In"
                            )}
                        </LoginButton>

                        <LoginSocialOptions>
                            <OrDivider>
                                <span>OR</span>
                            </OrDivider>

                            <SocialRow>
                                <CustomAppleSignIn onBeforeLogin={() => Promise.resolve(true)} />
                            </SocialRow>
                            <SocialRow>
                                <CustomGoogleSignIn
                                    preventRedirect
                                    onLoginSuccess={handleSocialSuccess}
                                />
                            </SocialRow>
                        </LoginSocialOptions>
                    </EmailLoginContent>
                )}
            </BottomSheet>
        </>
    );
};

const BrandLogo = styled.img`
    width: 176px;
    height: auto;
    align-self: center;
    margin-bottom: 8px;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 14px;
    right: 16px;
    border: none;
    background: none;
    font-size: 28px;
    line-height: 1;
    color: ${theme.colors.gray_1};
    cursor: pointer;
    padding: 4px 8px;
`;

const OfferHeadline = styled.h2`
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    margin: 0 0 12px;
    color: ${theme.colors.black};
    text-wrap: balance;
`;

const OfferField = styled.input`
    width: 100%;
    height: 46px;
    padding: 0 14px;
    margin: 10px 0;
    font-size: 16px;
    border: 1px solid ${theme.colors.gray_5};
    border-radius: ${theme.borderRadius.small};
    background: ${theme.colors.white};

    &::placeholder {
        color: ${theme.colors.gray_3};
    }

    &:focus {
        outline: none;
        border-color: ${theme.colors.main};
    }
`;

const ClaimButton = styled.button`
    width: 100%;
    height: 48px;
    margin-top: 12px;
    margin-bottom: 12px;
    border: none;
    border-radius: ${theme.borderRadius.small};
    background: ${theme.colors.main};
    color: ${theme.colors.button_text};
    font-size: 17px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const OrDivider = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    /* Equal either side, and tight: the claim button carries no bottom margin of
       its own, so this is the whole gap above as well as below. */
    margin: 2px 0;
    color: ${theme.colors.gray_3};
    font-size: 13px;
    font-weight: 600;

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background: ${theme.colors.gray_5};
    }
`;

const SocialRow = styled.div`
    width: 100%;
    margin-bottom: 8px;
`;

/* The sign-in step's providers. OrDivider is deliberately tight for the offer
   step, so the gap above the "OR" is given here instead of being added to the
   shared divider. */
const LoginSocialOptions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 20px;
`;

const LoginPrompt = styled.p`
    margin: 12px 0 0;
    text-align: center;
    font-size: 13px;
    color: ${theme.colors.gray_1};
`;

const LoginLink = styled.span`
    color: ${theme.colors.black};
    text-decoration: underline;
    cursor: pointer;
    font-weight: 600;
`;

const ChangeEmailLink = styled.button`
    display: block;
    margin: 4px auto 20px;
    border: none;
    background: none;
    color: ${theme.colors.highlight};
    font-size: 15px;
    text-decoration: underline;
    cursor: pointer;
`;

const OtpRow = styled.div`
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-bottom: 16px;
`;

const OtpBox = styled.input`
    width: 48px;
    height: 58px;
    text-align: center;
    font-size: 22px;
    font-weight: 600;
    border: 1px solid ${theme.colors.gray_5};
    border-radius: ${theme.borderRadius.small};

    &:focus {
        outline: none;
        border-color: ${theme.colors.main};
    }
`;

const ResendRow = styled.p`
    margin: 0 0 22px;
    text-align: center;
    font-size: 14px;
    color: ${theme.colors.gray_1};
`;

const ResendLink = styled.span<{ $disabled: boolean }>`
    color: ${(p) => (p.$disabled ? p.theme.colors.gray_3 : p.theme.colors.highlight)};
    cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
    font-weight: 600;
`;

const ForgotPasswordLink = styled.span<{ $disabled: boolean }>`
    display: block;
    margin: -4px 0 16px;
    text-align: right;
    font-size: 14px;
    font-weight: 600;
    color: ${(p) => (p.$disabled ? p.theme.colors.gray_4 : p.theme.colors.gray_2)};
    cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
`;

const slideUp = keyframes`
    from {
        transform: translateY(100%);
    }
    to {
        transform: translateY(0);
    }
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1000;
`;

const BottomSheet = styled.div`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${theme.colors.white};
    border-radius: ${theme.borderRadius.large} ${theme.borderRadius.large} 0 0;
    z-index: 1001;
    animation: ${slideUp} 0.3s ease-out;
    max-height: 90vh;
    overflow-y: auto;

    ${breakpoint.breakTablet`
        bottom: auto;
        top: 50%;
        left: 50%;
        right: auto;
        transform: translate(-50%, -50%);
        width: 450px;
        max-width: 90vw;
        border-radius: ${theme.borderRadius.large};
        animation: none;
    `}
`;

const MainContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 14px 16px 16px;
    gap: 0;
    position: relative;

    ${breakpoint.breakTablet`
        padding: 20px 24px 24px;
    `}
`;

const EmailLoginContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px 16px 40px;
    position: relative;

    ${breakpoint.breakTablet`
        padding: 32px 24px 40px;
    `}
`;

const BackButton = styled.button`
    position: absolute;
    top: 24px;
    left: 16px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;

    ${breakpoint.breakTablet`
        top: 32px;
        left: 24px;
    `}
`;

const Title = styled.h2`
    font-family: ${theme.fonts.secondary};
    font-weight: 600;
    font-size: 24px;
    line-height: 120%;
    text-align: center;
    color: ${theme.colors.black};
    margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
    font-family: "Plus Jakarta Sans", sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 150%;
    text-align: center;
    color: ${theme.colors.muted};
    margin: 0 0 24px 0;
    max-width: 361px;
`;

const InputLabel = styled.label`
    display: block;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    color: ${theme.colors.muted};
    margin-bottom: 8px;
    font-weight: 500;
`;

const EmailInput = styled.input`
    width: 100%;
    padding: 16px;
    border: 1px solid ${theme.colors.gray_5};
    border-radius: ${theme.borderRadius.medium};
    font-size: 16px;
    font-family: "Plus Jakarta Sans", sans-serif;
    margin-bottom: 16px;
    height: 52px;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${theme.colors.main};
    }

    &::placeholder {
        color: ${theme.colors.subtle};
    }

    &:disabled {
        background-color: ${theme.colors.gray_6};
        cursor: not-allowed;
    }
`;

const PasswordWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const PasswordInput = styled.input`
    width: 100%;
    padding: 16px 50px 16px 16px;
    border: 1px solid ${theme.colors.gray_5};
    border-radius: ${theme.borderRadius.medium};
    font-size: 16px;
    font-family: "Plus Jakarta Sans", sans-serif;
    margin-bottom: 20px;
    height: 52px;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: ${theme.colors.main};
    }

    &::placeholder {
        color: ${theme.colors.subtle};
    }
`;

const EyeIcon = styled.div`
    position: absolute;
    right: 16px;
    top: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: ${theme.colors.subtle};

    svg {
        width: 24px;
        height: 24px;
    }
`;

const LoginButton = styled.button<{ $isBusy?: boolean }>`
    width: 100%;
    height: 54px;
    border: none;
    border-radius: ${theme.borderRadius.small};
    /* Solid grey while signing in, brand gradient otherwise. */
    background: ${(p) =>
        p.$isBusy
            ? theme.colors.gray_3
            : `linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]})`};
    color: ${theme.colors.button_text};
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;

    /* The button is disabled both while a field is empty and while the request is
       in flight, and those want opposite treatments. Fading the whole element
       faded the spinner inside it too, which is what made the loader look broken;
       the busy state stays at full opacity and says so with the colour instead. */
    &:disabled {
        opacity: ${(p) => (p.$isBusy ? 1 : 0.5)};
        cursor: ${(p) => (p.$isBusy ? "progress" : "not-allowed")};
    }

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

export default SignInBottomSheetModal;

const PasswordHint = styled.p`
    width: 100%;
    margin: -12px 0 16px;
    font-size: 13px;
    color: ${theme.colors.gray_3};
`;

const SkipLink = styled.button`
    display: block;
    margin: 14px auto 0;
    border: none;
    background: none;
    color: ${theme.colors.gray_1};
    font-size: 14px;
    text-decoration: underline;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const FinishingDialog = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1300;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 24px 28px;
    border-radius: ${theme.borderRadius.large};
    background: ${theme.colors.white};
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
    text-align: center;
`;

const FinishingTitle = styled.p`
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    color: ${theme.colors.black};
`;
